import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import GripLogo from '../assets/grip-logo.png';
import GripWallpaper from '../assets/grip-wallpaper.jpg';
import toast from 'react-hot-toast';

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showApprovalMessage, setShowApprovalMessage] = useState(false);
  const navigate = useNavigate();
  const { signup } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(name, email, password);
      setShowApprovalMessage(true);
    } catch (error) {
      // Error is handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  if (showApprovalMessage) {
    return (
      <Container>
        <Background />
        <SignupCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Logo>
            <LogoImage src={GripLogo} alt="GRIP Logo" />
          </Logo>

          <Title>Thank You for Signing Up!</Title>
          <ApprovalMessage>
            <ApprovalIcon>✓</ApprovalIcon>
            <ApprovalTitle>Request Sent for Approval</ApprovalTitle>
            <ApprovalText>
              Your account has been created successfully. Your request has been sent for admin approval.
            </ApprovalText>
            <ApprovalText>
              You'll be able to login after your account is approved. You'll receive an email confirmation once your account is approved.
            </ApprovalText>
            <ApprovalText style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: '#718096' }}>
              In the meantime, you can check your email for further instructions.
            </ApprovalText>
          </ApprovalMessage>

          <Footer>
            <FooterText>
              Already approved?{' '}
              <SignInLink onClick={() => navigate('/login')}>Sign In</SignInLink>
            </FooterText>
          </Footer>
        </SignupCard>
      </Container>
    );
  }

  return (
    <Container>
      <Background />
      <SignupCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Logo>
          <LogoImage src={GripLogo} alt="GRIP Logo" />
        </Logo>

        <Title>Create Account</Title>
        <Subtitle>Sign up to get started with GRIP</Subtitle>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>Full Name</Label>
            <Input
              type="text"
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
            />
          </InputGroup>

          <InputGroup>
            <Label>Email Address</Label>
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </InputGroup>

          <InputGroup>
            <Label>Password</Label>
            <Input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </InputGroup>

          <InputGroup>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
            />
          </InputGroup>

          <SignupButton
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </SignupButton>
        </Form>

        <Footer>
          <FooterText>
            Already have an account?{' '}
            <SignInLink onClick={() => navigate('/login')}>Sign In</SignInLink>
          </FooterText>
        </Footer>
      </SignupCard>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
  position: relative;
  overflow: hidden;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${GripWallpaper});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
  }
`;

const SignupCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 3rem;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  // margin-bottom: 2rem;
`;

const LogoImage = styled.img`
  height: 120px;
  width: auto;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 64px;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #718096;
  text-align: center;
  margin: 0 0 2rem 0;
  font-size: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #cbd5e0;
  }
`;

const SignupButton = styled(motion.button)`
  padding: 1rem;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 107, 53, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const FooterText = styled.p`
  color: #a0aec0;
  font-size: 0.875rem;
  margin: 0;
`;

const SignInLink = styled.span`
  color: #ff6b35;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #f7931e;
  }
`;

const ApprovalMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 0;
`;

const ApprovalIcon = styled.div`
  font-size: 3rem;
  color: #48bb78;
  margin-bottom: 1rem;
  font-weight: bold;
`;

const ApprovalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
`;

const ApprovalText = styled.p`
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export default Signup;
